import { useMemo, useState, type MouseEvent, type SyntheticEvent } from "react";
import { Link as RouterLink } from "react-router";
import { useTheme } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box, { type BoxProps } from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ArrowForward } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  dashboardPanelChildNavArrowIcon,
  dashboardPanelChildNavButton,
} from "../styles/dashboardPanel";
import {
  getDashboardChildSectionNavStyles,
  type DashboardChildSectionNavVariant,
} from "../styles/drawer";
import { groupChildDashboardsBySection } from "../utils/groupChildDashboardsBySection";
import type { DashboardChildNavItem } from "../utils/types";

export interface DashboardChildNavListProps {
  readonly childDashboards?: DashboardChildNavItem[];
  readonly parentSection?: string | null;
  readonly onNavigate?: () => void;
  readonly onDashboardClick?: (
    event: MouseEvent<HTMLAnchorElement>,
    id: number,
  ) => void;
  readonly wrapperSx?: SxProps<Theme>;
  readonly variant?: DashboardChildSectionNavVariant;
}

function ChildDashboardButtons({
  dashboards,
  onNavigate,
  onDashboardClick,
}: Readonly<{
  dashboards: DashboardChildNavItem[];
  onNavigate?: () => void;
  onDashboardClick?: (
    event: MouseEvent<HTMLAnchorElement>,
    id: number,
  ) => void;
}>) {
  return (
    <>
      {dashboards.map((child) => (
        <Button
          key={child.id}
          component={RouterLink}
          to={`/dashboard/${child.id}`}
          variant="text"
          disableRipple
          onClick={(event: MouseEvent<HTMLAnchorElement>) => {
            onDashboardClick?.(event, child.id);
            if (!event.defaultPrevented) {
              onNavigate?.();
            }
          }}
          startIcon={<ArrowForward sx={dashboardPanelChildNavArrowIcon} />}
          sx={dashboardPanelChildNavButton}
        >
          {child.title}
        </Button>
      ))}
    </>
  );
}

export default function DashboardChildNavList({
  childDashboards,
  parentSection,
  onNavigate,
  onDashboardClick,
  wrapperSx,
  variant = "panel",
}: DashboardChildNavListProps) {
  const theme = useTheme();
  const styles = useMemo(
    () => getDashboardChildSectionNavStyles(theme, variant),
    [theme, variant],
  );

  const sectionGroups = useMemo(
    () => groupChildDashboardsBySection(childDashboards, parentSection),
    [childDashboards, parentSection],
  );

  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  if (!sectionGroups.length) {
    return null;
  }

  const boxSx = (
    wrapperSx ? [styles.root, wrapperSx] : styles.root
  ) as BoxProps["sx"];

  const handleSectionToggle =
    (sectionName: string) => (_event: SyntheticEvent, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? sectionName : false);
    };

  const stopAccordionBubble = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Box sx={boxSx} onClick={stopAccordionBubble}>
      {sectionGroups.map((group) => {
        const isExpanded = expandedSection === group.name;
        const dashboardLabel =
          group.dashboards.length === 1 ? "Dashboard" : "Dashboards";

        return (
          <Accordion
            key={group.name}
            expanded={isExpanded}
            onChange={handleSectionToggle(group.name)}
            disableGutters
            elevation={0}
            square={variant !== "card"}
            sx={styles.accordion}
          >
            <AccordionSummary
              expandIcon={
                <img src="/images/down-arrow.svg" alt="Expand section" />
              }
              aria-controls={`child-section-${group.name}-content`}
              id={`child-section-${group.name}-header`}
              onClick={stopAccordionBubble}
              sx={styles.accordionSummary}
            >
              <Box sx={styles.accordionSummaryContent}>
                <Typography sx={styles.sectionTitle} variant="h6">
                  {group.name}
                </Typography>
                <Typography sx={styles.sectionSubtitle} variant="subtitle1">
                  {group.dashboards.length} {dashboardLabel}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={styles.accordionDetails}
              onClick={stopAccordionBubble}
            >
              <Box sx={styles.childNavList}>
                <ChildDashboardButtons
                  dashboards={group.dashboards}
                  onNavigate={onNavigate}
                  onDashboardClick={onDashboardClick}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
